import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { attributeTypeService } from '@/services/attributeTypeService';
import { projectAttributeTypeService } from '@/services/projectAttributeTypeService';
import { AttributeType } from '@/types';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { useParams } from 'react-router-dom';

export function AttributeDataPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Extract project ID from IRI
  const projectId = selectedProject ? parseInt(selectedProject.replace('/api/projects/', ''), 10) : null;
  const isProjectScoped = !!routeProjectId;

  useEffect(() => {
    if (routeProjectId) {
      const id = Number(routeProjectId);
      if (Number.isFinite(id) && id > 0) {
        setSelectedProject(`/api/projects/${id}`);
      }
    }
  }, [routeProjectId]);

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      // Use projectService if available, or fetch via API
      try {
        const response = await fetch(`/api/projects/${projectId}`);
        return await response.json();
      } catch (e) {
        return null;
      }
    },
    enabled: !!projectId,
  });

  // Fetch all attribute types
  const {
    data: attributeTypes = [],
    isLoading: isLoadingAttributeTypes,
  } = useQuery({
    queryKey: ['attributeTypes'],
    queryFn: () => attributeTypeService.getAttributeTypes(),
  });

  // Fetch project attribute types for selected project
  const {
    data: projectAttributeTypes = [],
    isLoading: isLoadingProjectAttributeTypes,
  } = useQuery({
    queryKey: ['projectAttributeTypes', projectId],
    queryFn: () => {
      if (!projectId) return [];
      return projectAttributeTypeService.getByProject(projectId);
    },
    enabled: !!projectId,
  });

  // Create a map of attributeTypeId -> loadData
  const loadDataMap = useMemo(() => {
    const map = new Map<number, boolean>();
    projectAttributeTypes.forEach((pat) => {
      map.set(pat.attributeType.id, pat.loadData);
    });
    return map;
  }, [projectAttributeTypes]);

  // Update project attribute type mutation
  const updateMutation = useMutation({
    mutationFn: ({ attributeTypeId, loadData }: { attributeTypeId: number; loadData: boolean }) => {
      if (!projectId) throw new Error('No project selected');
      return projectAttributeTypeService.updateLoadData(projectId, attributeTypeId, loadData);
    },
    onMutate: async ({ attributeTypeId, loadData }) => {
      if (!projectId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['projectAttributeTypes', projectId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<typeof projectAttributeTypes>(['projectAttributeTypes', projectId]);

      // Optimistically update the cache
      queryClient.setQueryData<typeof projectAttributeTypes>(['projectAttributeTypes', projectId], (old = []) => {
        const existing = old.find((pat) => pat.attributeType.id === attributeTypeId);
        if (existing) {
          // Update existing
          return old.map((pat) =>
            pat.attributeType.id === attributeTypeId ? { ...pat, loadData } : pat
          );
        } else {
          // Create new entry (will be created on server)
          const attributeType = attributeTypes.find((at) => at.id === attributeTypeId);
          if (!attributeType) return old;
          
          return [
            ...old,
            {
              id: 0, // Temporary ID
              project: { id: projectId, title: '' },
              attributeType: {
                id: attributeType.id,
                code: attributeType.code,
                name: attributeType.name,
              },
              loadData,
            },
          ];
        }
      });

      return { previousData };
    },
    onSuccess: (serverRow) => {
      // No refetch needed: reconcile cache with server response (mainly to get the real id)
      if (projectId) {
        queryClient.setQueryData<typeof projectAttributeTypes>(['projectAttributeTypes', projectId], (old = []) => {
          const idx = old.findIndex((pat) => pat.attributeType.id === serverRow.attributeType.id);
          if (idx === -1) return [...old, serverRow];
          return old.map((pat, i) => (i === idx ? serverRow : pat));
        });
      }
      setToastMessage('Configuration mise à jour avec succès !');
      setToastType('success');
      setShowToast(true);
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['projectAttributeTypes', projectId], context.previousData);
      }
      setToastMessage('Erreur lors de la mise à jour de la configuration');
      setToastType('error');
      setShowToast(true);
    },
  });

  const handleToggleLoadData = (attributeType: AttributeType) => {
    if (!projectId) {
      setToastMessage('Veuillez sélectionner un projet d\'abord');
      setToastType('error');
      setShowToast(true);
      return;
    }
    const currentLoadData = loadDataMap.get(attributeType.id) ?? false;
    const newLoadData = !currentLoadData;
    updateMutation.mutate({ attributeTypeId: attributeType.id, loadData: newLoadData });
  };

  const isLoading = isLoadingAttributeTypes || isLoadingProjectAttributeTypes;

  // Filter attribute types by search query
  const filteredAttributeTypes = attributeTypes.filter((at) =>
    at.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    at.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show selected (loadData=true) first
  const sortedAttributeTypes = useMemo(() => {
    return [...filteredAttributeTypes].sort((a, b) => {
      const aSelected = loadDataMap.get(a.id) ?? false;
      const bSelected = loadDataMap.get(b.id) ?? false;
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }
      // Stable secondary sort
      return a.code.localeCompare(b.code);
    });
  }, [filteredAttributeTypes, loadDataMap]);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Configuration</span> / Attribute data
          </div>
          {project && (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
              {project.city && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-4">
            <div className="relative hidden sm:block">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-icons-outlined text-slate-400 text-lg">search</span>
              </span>
              <input
                className="pl-10 pr-4 py-2 w-64 text-sm bg-slate-50 dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-full focus:outline-none focus:ring-2 focus:ring-primary dark:text-white placeholder-slate-400"
                placeholder="Rechercher..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
            <button className="relative p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors">
              <span className="material-icons-outlined">notifications</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Attribute Data</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Configurez quels types d'attributs doivent charger des données par projet.
              </p>
            </div>
          </div>

          {/* Project Selection (only for non-project-scoped route) */}
          {!isProjectScoped && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                Sélectionner un projet *
              </label>
              <ProjectSearchSelect value={selectedProject} onChange={setSelectedProject} />
            </div>
          )}

          {!selectedProject && !isProjectScoped && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Veuillez sélectionner un projet pour configurer les types d'attributs.
              </p>
            </div>
          )}

          {/* Attribute Types Table */}
          {!selectedProject ? (
            <div className="text-center py-12 text-slate-500">Sélectionnez un projet pour voir les types d'attributs</div>
          ) : isLoading ? (
            <div className="text-center py-12">Chargement...</div>
          ) : sortedAttributeTypes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Aucun type d'attribut trouvé</div>
          ) : (
            <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-[#1E293B]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Unité
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Load Data
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                  {sortedAttributeTypes.map((attributeType) => (
                    <tr key={attributeType.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">
                        {attributeType.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                        {attributeType.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                        {attributeType.unit || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleToggleLoadData(attributeType)}
                          disabled={updateMutation.isPending || !selectedProject}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            loadDataMap.get(attributeType.id) ?? false
                              ? 'bg-primary'
                              : 'bg-slate-200 dark:bg-slate-700'
                          } ${updateMutation.isPending || !selectedProject ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          role="switch"
                          aria-checked={loadDataMap.get(attributeType.id) ?? false}
                          aria-label={`Toggle load data for ${attributeType.name}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              loadDataMap.get(attributeType.id) ?? false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Toast */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
